import { useMutation } from "@tanstack/react-query";
import { QueryKeys, client, queryClient } from "queryClient";
import { CreateBookingInput, InfiniteBookingData } from "types";
import { getBooking } from "modules/bookings/api";

export const useBookingCreate = () => {
  const createMutation = useMutation({
    mutationFn: async (input: CreateBookingInput) => {
      const { data: newBooking } = await client.models.Booking.create(input);

      if (!newBooking) return null;

      const booking = await getBooking(newBooking.id);

      return booking;
    },
    // When mutate is called:
    onMutate: async (newBooking) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Bookings] });

      // Snapshot the previous value
      const previousBookings = queryClient.getQueryData([QueryKeys.Bookings]);

      // Optimistically update to the new value
      if (previousBookings) {
        queryClient.setQueryData(
          [QueryKeys.Bookings],
          (old: InfiniteBookingData) => {
            const oldData = old?.pages
              ? old.pages.flatMap((page) => page.bookingList)
              : [];

            [...oldData, newBooking];
          },
        );
      }

      // Return a context object with the snapshotted value
      return { previousBookings };
    },
    // If the mutation fails,
    // use the context returned from onMutate to rollback
    onError: (err, newBooking, context) => {
      console.error("Error saving record:", err, newBooking);
      if (context?.previousBookings) {
        queryClient.setQueryData(
          [QueryKeys.Bookings],
          context.previousBookings,
        );
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Bookings] });
    },
  });

  return createMutation;
};
